import { StatusDot } from "@/components/ui/status-dot";
import { Button, DenkerThinkingLogo } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { surfaceRoleAttributes } from "@/components/ui/surface-contract";
import { UNDOCKED_AGENT_STATUS_GROUP } from "@/desktop/native-glass-densify";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import type { ComponentPropsWithRef, ReactNode, SyntheticEvent } from "react";
import { AgentStatusText } from "./agent-status-text";
import "./agent-status-surface.css";

type CursorDisplayStatus =
  | { kind: "error"; text: string }
  | { kind: "tool"; text: string; failed?: boolean }
  | { kind: "reply"; text: string }
  | { kind: "thinking" };

interface AgentStatusSurfaceProps {
  agentName: string;
  status: CursorDisplayStatus;
  expanded: boolean;
  hasMore: boolean;
  maxHeight?: number;
  onToggleExpand: () => void;
  onPointerDownControl: (event: SyntheticEvent) => void;
  overlayControls?: ReactNode;
  overlayControlCount?: number;
  testId: string;
}

/** Shared cursor material so compact and workspace status use one native
 * surface contract while owning their content and motion independently. */
export function AgentStatusMaterial({
  className,
  children,
  ...props
}: ComponentPropsWithRef<"div">) {
  return (
    <div
      {...surfaceRoleAttributes("rail", {
        nativeLevel: "island",
        nativeGroup: UNDOCKED_AGENT_STATUS_GROUP,
      })}
      {...props}
      className={cn("denker-agent-status-material", className)}
    >
      {children}
    </div>
  );
}

/** Neutral undocked presence surface. Thinking is a compact Denker orb;
 * tool and reply updates expand the same material into a readable card. */
export function AgentStatusSurface({
  agentName,
  status,
  expanded,
  hasMore,
  maxHeight,
  onToggleExpand,
  onPointerDownControl,
  overlayControls,
  overlayControlCount = 0,
  testId,
}: AgentStatusSurfaceProps) {
  const t = useT(cursorsMessages);
  const thinking = status.kind === "thinking";
  const isError = status.kind === "error";

  return (
    <AgentStatusMaterial
      className={cn(
        "relative flex w-max shrink-0 overflow-hidden",
        "[interpolate-size:allow-keywords] transition-[min-width,max-width,height,padding] duration-[180ms] ease-out motion-reduce:transition-none",
        thinking
          ? "h-9 min-w-9 max-w-9 items-center justify-center p-0"
          : cn(
              "min-h-9 min-w-0 max-w-[340px] items-center py-2",
              overlayControlCount === 1 ? "pl-2.5 pr-7" : "px-2.5",
            ),
      )}
      style={{
        maxHeight: expanded ? maxHeight : undefined,
      }}
      data-agent-status-kind={status.kind}
      data-cursor-tone={isError ? "error" : "normal"}
      data-testid={`${testId}-bubble`}
      aria-label={
        status.kind === "thinking"
          ? t("agentThinking", { agentName })
          : `${agentName}: ${status.text}`
      }
    >
      {overlayControls}
      {thinking ? (
        <DenkerThinkingLogo
          className={cn(
            "h-4 w-4 transition-opacity",
            overlayControlCount > 0 &&
              "denker-thinking-logo--hide-for-controls",
          )}
          height={13}
          testId={`${testId}-thinking`}
        />
      ) : (
        <div
          className="flex min-w-0 flex-1 items-start"
          data-testid={`${testId}-status-row`}
        >
          <div
            className="flex min-w-0 flex-1 items-start gap-1 overflow-y-auto text-appkit-caption font-normal leading-4"
            style={{
              maxHeight:
                expanded && maxHeight ? Math.max(maxHeight - 16, 0) : undefined,
            }}
            data-testid={`${testId}-scroll-region`}
          >
            {status.kind === "tool" ? (
              <span
                className="flex min-w-0 items-center gap-1.5"
                data-testid={`${testId}-tool-row`}
              >
                <AgentStatusText
                  text={status.text}
                  working={!status.failed}
                  className="min-w-0 flex-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                  data-testid={`${testId}-tool`}
                />
                {status.failed ? (
                  <span
                    className="flex h-[1lh] shrink-0 items-center"
                    title={t("toolFailed")}
                    data-testid={`${testId}-tool-failed-dot`}
                  >
                    <StatusDot tone="error" label={t("toolFailed")} />
                  </span>
                ) : null}
              </span>
            ) : status.kind === "error" ? (
              <span
                className="min-w-0 whitespace-pre-wrap break-words text-danger-light [overflow-wrap:anywhere]"
                data-testid={`${testId}-error`}
              >
                {status.text}
              </span>
            ) : (
              <div
                className="min-w-0 flex-1 break-words [overflow-wrap:anywhere] [&_*]:text-inherit"
                data-testid={`${testId}-content`}
              >
                <MarkdownContent
                  content={status.text}
                  className="leading-4 [&_li]:leading-4 [&_ol]:pl-4 [&_p]:leading-4 [&_ul]:pl-3"
                />
              </div>
            )}
            {status.kind === "reply" && hasMore ? (
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onPointerDown={onPointerDownControl}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpand();
                }}
                className="-mr-1 -mt-1 ml-0.5 shrink-0 text-muted hover:bg-glass-fill-heavy hover:text-primary"
                aria-label={expanded ? t("collapseReply") : t("expandReply")}
                aria-expanded={expanded}
                data-testid={`${testId}-expand`}
              >
                {expanded ? (
                  <Icons.ChevronUp className="h-3 w-3" />
                ) : (
                  <Icons.ChevronDown className="h-3 w-3" />
                )}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </AgentStatusMaterial>
  );
}

export type { CursorDisplayStatus };
